import { useEffect } from "react";

interface ScrollToTopOptions {
  /** Comportamiento del scroll: smooth (suave), instant (instantáneo), auto */
  behavior?: ScrollBehavior;
  /** Posición vertical a la que scrollear (0 = top) */
  top?: number;
  /** Valor que al cambiar, dispara el scroll. Ej: currentStep, productId, etc. */
  trigger?: any;
}

/**
 * Hook que hace scroll automático cuando cambia un valor trigger.
 *
 * @example
 * // Scroll al top cuando cambia el step
 * const [currentStep, setCurrentStep] = useState("form");
 * useScrollToTop({ trigger: currentStep });
 *
 * @example
 * // Scroll instantáneo cuando cambia el producto
 * useScrollToTop({ trigger: productId, behavior: "instant" });
 */
export function useScrollToTop(options: ScrollToTopOptions = {}) {
  const { behavior = "smooth", top = 0, trigger } = options;

  useEffect(() => {
    window.scrollTo({
      top,
      left: 0,
      behavior,
    });
  }, [trigger, behavior, top]);
}

/**
 * Función helper para hacer scroll manual cuando lo necesites.
 *
 * @example
 * const handleSubmit = () => {
 *   // ... tu lógica
 *   scrollToTop("smooth");
 * };
 */
export function scrollToTop(behavior: ScrollBehavior = "smooth") {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior,
  });
}

/**
 * Hook para hacer scroll a un elemento específico del DOM.
 *
 * @example
 * const formRef = useRef<HTMLDivElement>(null);
 * useScrollToElement(formRef, currentStep);
 *
 * return <div ref={formRef}>...</div>
 */
export function useScrollToElement(
  elementRef: React.RefObject<HTMLElement>,
  trigger?: any
) {
  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [trigger, elementRef]);
}
